// Employee UI locale: persisted in localStorage ('tym-locale') and synced to
// users.locale on the server when authenticated. index.html reads the same
// key before first paint for document.documentElement.lang.

export type LocaleCode = 'en' | 'kn' | 'or' | 'hi';

const KEY = 'tym-locale';
const VALID = new Set<LocaleCode>(['en', 'kn', 'or', 'hi']);

function readPref(): LocaleCode {
  const v = localStorage.getItem(KEY);
  return v && VALID.has(v as LocaleCode) ? (v as LocaleCode) : 'en';
}

function applyLang(code: LocaleCode) {
  document.documentElement.lang = code;
  document.documentElement.classList.toggle('tym-locale-indic', code !== 'en');
}

class LocaleState {
  current = $state<LocaleCode>(readPref());
  /** Set when user picks a language on the login screen before auth completes. */
  changedBeforeLogin = $state(false);

  constructor() {
    applyLang(this.current);
  }

  set(code: LocaleCode, opts?: { persist?: boolean; fromLoginScreen?: boolean }) {
    if (!VALID.has(code)) return;
    this.current = code;
    applyLang(code);
    if (opts?.persist !== false) {
      localStorage.setItem(KEY, code);
    }
    if (opts?.fromLoginScreen) {
      this.changedBeforeLogin = true;
    }
  }

  /** Apply server-stored locale after auth (server wins unless login-screen override pending). */
  applyFromUser(userLocale: string | undefined) {
    if (this.changedBeforeLogin) return;
    const code = userLocale && VALID.has(userLocale as LocaleCode) ? (userLocale as LocaleCode) : 'en';
    this.set(code);
  }

  clearLoginOverride() {
    this.changedBeforeLogin = false;
  }
}

export const locale = new LocaleState();
